import { Button, Center, Flex, Input, Spinner, Text, useToast } from '@chakra-ui/react';
import { FC, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { useMutationApi } from '@/shared/api/useMutationApi';
import Header from '@/shared/components/layouts/Header';

const VerifyEmail: FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const recaptchaRef = useRef<any>(null);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);

  const handleEmailSent = () => {
    toast({
      title: `${t('Verification email sent to')} ${email.toLowerCase()}`,
      status: 'success',
    });
    // Reset reCAPTCHA after success
    recaptchaRef.current?.reset();
    setCaptchaValue(null);
  };

  const handleEmailVerificationError = () => {
    toast({
      title: t('Error sending verification email'),
      status: 'error',
    });
    // Reset reCAPTCHA on error
    recaptchaRef.current?.reset();
    setCaptchaValue(null);
  };

  const [{ loading }, resendEmailVerification] = useMutationApi(
    {
      method: 'POST',
      url: `/auth0/users/resend-verification-email`,
    },
    {
      onSuccess: handleEmailSent,
      onError: handleEmailVerificationError,
    }
  );

  const handleResendEmailVerification = async () => {
    const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    if (!email || !validRegex.test(email)) {
      toast({
        title: t('Please enter a valid email'),
        status: 'error',
      });
      return;
    }

    if (!captchaValue) {
      toast({
        title: t('Please complete the CAPTCHA'),
        status: 'error',
      });
      return;
    }

    // Normalize email to lowercase to ensure case-insensitive matching
    const normalizedEmail = email.toLowerCase();
    await resendEmailVerification({ data: { email: normalizedEmail, captcha: captchaValue } });
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };



  return (
    <>
      <Helmet>
        <title>Email Not Verified</title>
      </Helmet>
      <Header hideProfile={true} />
      <Center h="100vh">
        <Flex direction="column" justifyContent="space-between" alignItems="center" mb="4" h={72}>
          {loading ? (
            <Spinner />
          ) : (
            <>
              <Text textAlign="center" mb={4}>
                {t(
                  "We've sent a verification link from NoReplyAuth0@sandiego.gov to your inbox. Feel free to close this tab once you have verified your email."
                )}
              </Text>
              <Text textAlign="center" fontSize="sm" mb={4}>
                {t("Didn't receive the email? Enter your email address below and complete the CAPTCHA to resend the verification email.")}
              </Text>
              <Flex direction="column" alignItems="center">
                <Input
                  placeholder={t('Email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  mb={4}
                  width="300px"
                />
                <div style={{ marginBottom: '16px' }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || 'DUMMY'}
                    onChange={handleCaptchaChange}
                  />
                </div>
                <Button 
                  size={'sm'} 
                  onClick={handleResendEmailVerification}
                  isDisabled={!captchaValue || loading}
                  isLoading={loading}
                  colorScheme="blue"
                  width="300px"
                >
                  {t('Resend email verification')}
                </Button>
              </Flex>
            </>
          )}
        </Flex>
      </Center>
    </>
  );
};

export default VerifyEmail;
